// Persistent Word & Translation Storage and Server Synchronization with IndexedDB Auto-Healing
// Allows admin edits of Kabyle words, French translations, and emojis to persist permanently across all devices,
// surviving browser cache clears and Cloud container server restarts.
import {
  saveWordOverrideToFirestore,
  deleteWordOverrideFromFirestore,
  subscribeFirestoreWordOverrides,
  fetchAllWordOverridesFromFirestore,
} from '../lib/firebase';
import { deleteRecordedAudio } from './audioStorage';
import { classifyWordCategory } from './wordClassifier';

export interface WordOverride {
  kab: string;
  fr: string;
  icon?: string;
  type?: string;
  isNew?: boolean;
  isDeleted?: boolean;
  gender?: 'm' | 'f' | 'none';
  grammaticalNumber?: 'sg' | 'pl';
  pluralKab?: string;
  singularKab?: string;
  feminineKab?: string;
  masculineKab?: string;
  variants?: string;
  conjugationAccompli?: string;
  conjugationInaccompli?: string;
}

export type WordOverridesMap = Record<string, WordOverride>;

const STORAGE_KEY = 'kabyleGame_wordOverrides';
const IDB_NAME = 'tamazight_word_overrides_db';
const IDB_STORE = 'overrides_store';
const IDB_KEY = 'all_overrides_map';

// In-memory cache for instant zero-latency UI rendering
let memoryOverrides: WordOverridesMap = {};
const listeners = new Set<(overrides: WordOverridesMap) => void>();
let isAutoHealingServer = false;

// ==========================================
// IndexedDB Persistent Storage
// ==========================================
function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not supported'));
    }
    const request = indexedDB.open(IDB_NAME, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getStoredOverridesFromIDB(): Promise<WordOverridesMap | null> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

export async function setStoredOverridesInIDB(overrides: WordOverridesMap): Promise<void> {
  try {
    const db = await getIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(overrides, IDB_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('Could not store word overrides in IndexedDB:', e);
  }
}

// 1. Initialize immediately from localStorage on startup to prevent any layout shifts or flicker
try {
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        memoryOverrides = parsed;
      }
    }
  }
} catch (e) {
  console.warn('Could not read cached word overrides from localStorage:', e);
}

// 2. Also check IndexedDB on boot
if (typeof window !== 'undefined') {
  getStoredOverridesFromIDB().then((idbOverrides) => {
    if (idbOverrides && Object.keys(idbOverrides).length > 0) {
      memoryOverrides = { ...memoryOverrides, ...idbOverrides };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
      } catch {}
      notifyListeners();
    }
  });

  // 3. Realtime Firestore sync for word overrides
  try {
    subscribeFirestoreWordOverrides((cloudOverrides) => {
      if (cloudOverrides && Object.keys(cloudOverrides).length > 0) {
        memoryOverrides = { ...memoryOverrides, ...cloudOverrides };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
        } catch {}
        setStoredOverridesInIDB(memoryOverrides);
        notifyListeners();
      }
    });
  } catch (err) {
    console.warn('Could not start Firestore word overrides listener:', err);
  }

  // 4. Initial fetch and bidirectional reconciliation with Firestore
  syncWordOverridesWithFirestore().catch((err) => {
    console.warn('Initial Firestore word overrides sync failed:', err);
  });
}

function notifyListeners(): void {
  listeners.forEach((fn) => {
    try {
      fn(memoryOverrides);
    } catch (e) {
      console.error('Error in word overrides listener:', e);
    }
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kabyle_words_updated', { detail: memoryOverrides }));
  }
}

/**
 * Synchronize all word overrides from the backend server database
 * and auto-heal from IndexedDB/localStorage if the server container restarted and lost data.
 */
export async function syncWordOverridesFromServer(): Promise<WordOverridesMap> {
  if (typeof window === 'undefined') return memoryOverrides;

  try {
    const res = await fetch('/api/word-overrides');
    if (res.ok) {
      const data = await res.json();
      const serverOverrides: WordOverridesMap = (data && data.overrides) ? data.overrides : {};
      const serverCount = Object.keys(serverOverrides).length;

      // Check local stores (IndexedDB, localStorage, and in-memory cache)
      const idbOverrides = await getStoredOverridesFromIDB();
      let localOverrides: WordOverridesMap = {};
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) localOverrides = JSON.parse(raw);
      } catch {}

      const bestLocal = { ...memoryOverrides, ...(idbOverrides || {}), ...localOverrides };
      const localCount = Object.keys(bestLocal).length;

      if (serverCount === 0 && localCount > 0) {
        // Server container was recreated/restarted! Auto-heal server from client persistent storage
        console.log(`Auto-healing server word overrides from client storage (${localCount} words)...`);
        memoryOverrides = { ...bestLocal };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
        await setStoredOverridesInIDB(memoryOverrides);
        notifyListeners();

        if (!isAutoHealingServer) {
          isAutoHealingServer = true;
          fetch('/api/word-overrides', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ batch: bestLocal }),
          })
            .then((r) => r.json())
            .then((resp) => {
              if (resp.success) {
                console.log('✅ Word overrides auto-healing completed on server!');
              }
            })
            .catch((err) => console.warn('Could not auto-heal word overrides on server:', err))
            .finally(() => {
              isAutoHealingServer = false;
            });
        }
        return memoryOverrides;
      }

      // If server has data, check if there are local keys to migrate
      const needsMigrationKeys = Object.keys(bestLocal).filter((k) => !serverOverrides[k]);
      if (needsMigrationKeys.length > 0) {
        const batchToPush: WordOverridesMap = {};
        needsMigrationKeys.forEach((k) => {
          batchToPush[k] = bestLocal[k];
        });

        fetch('/api/word-overrides', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch: batchToPush }),
        }).catch((err) => console.warn('Could not push local overrides batch:', err));
      }

      // Combine server and local, never losing local edits
      memoryOverrides = { ...bestLocal, ...serverOverrides };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
      await setStoredOverridesInIDB(memoryOverrides);
      notifyListeners();
      return memoryOverrides;
    }
  } catch (err) {
    console.warn('Could not fetch word overrides from server, using local cache:', err);
    const idbOverrides = await getStoredOverridesFromIDB();
    if (idbOverrides) {
      memoryOverrides = { ...idbOverrides, ...memoryOverrides };
      notifyListeners();
    }
  }

  return memoryOverrides;
}

/**
 * Save a modified word (Kabyle, French, Icon) to server, IndexedDB, and local cache
 */
export async function saveWordOverride(
  originalKab: string,
  kab: string,
  fr: string,
  icon?: string,
  type?: string,
  isNew?: boolean,
  gender?: 'm' | 'f' | 'none',
  pluralKab?: string,
  singularKab?: string,
  grammaticalNumber?: 'sg' | 'pl',
  variants?: string,
  conjugationAccompli?: string,
  conjugationInaccompli?: string,
  feminineKab?: string,
  masculineKab?: string
): Promise<{ success: boolean; overrides: WordOverridesMap }> {
  const normalizedOriginal = originalKab.trim();
  let resolvedType = type?.trim() || 'Général';
  if (resolvedType.toLowerCase() === 'général' || resolvedType.toLowerCase() === 'general') {
    resolvedType = classifyWordCategory({ kab: kab.trim(), fr: fr.trim(), type: resolvedType });
  }
  const override: WordOverride = {
    kab: kab.trim(),
    fr: fr.trim(),
    icon: icon?.trim() || undefined,
    type: resolvedType,
    isNew: Boolean(isNew),
    isDeleted: false,
    gender: gender === 'm' || gender === 'f' ? gender : (gender === 'none' ? 'none' : undefined),
    pluralKab: pluralKab !== undefined ? pluralKab.trim() : undefined,
    singularKab: singularKab !== undefined ? singularKab.trim() : undefined,
    feminineKab: feminineKab !== undefined ? feminineKab.trim() : undefined,
    masculineKab: masculineKab !== undefined ? masculineKab.trim() : undefined,
    grammaticalNumber,
    variants: variants?.trim() || undefined,
    conjugationAccompli: conjugationAccompli?.trim() || undefined,
    conjugationInaccompli: conjugationInaccompli?.trim() || undefined,
  };

  // Immediate optimistic update in memory, local storage, and IndexedDB
  memoryOverrides = {
    ...memoryOverrides,
    [normalizedOriginal]: override,
    [normalizedOriginal.toLowerCase()]: override,
  };

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
    } catch {}
    await setStoredOverridesInIDB(memoryOverrides);
  }
  notifyListeners();

  // Persist to server backend database
  try {
    const res = await fetch('/api/word-overrides', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        originalKab: normalizedOriginal,
        kab: override.kab,
        fr: override.fr,
        icon: override.icon,
        type: override.type,
        isNew: override.isNew,
        gender: override.gender,
        grammaticalNumber: override.grammaticalNumber,
        pluralKab: override.pluralKab,
        singularKab: override.singularKab,
        feminineKab: override.feminineKab,
        masculineKab: override.masculineKab,
        variants: override.variants,
        conjugationAccompli: override.conjugationAccompli,
        conjugationInaccompli: override.conjugationInaccompli,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data && data.overrides) {
        memoryOverrides = { ...memoryOverrides, ...data.overrides };
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
          } catch {}
          await setStoredOverridesInIDB(memoryOverrides);
        }
        notifyListeners();
        saveWordOverrideToFirestore(normalizedOriginal, override).catch(() => {});
        return { success: true, overrides: memoryOverrides };
      }
    }
  } catch (err) {
    console.error('Failed to save word override to server, kept in local storage:', err);
  }

  saveWordOverrideToFirestore(normalizedOriginal, override).catch(() => {});
  return { success: true, overrides: memoryOverrides };
}

/**
 * Import a full batch of overrides (used for Restore / Backup)
 */
export async function importWordOverridesBatch(batch: WordOverridesMap): Promise<boolean> {
  try {
    memoryOverrides = { ...memoryOverrides, ...batch };
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
      } catch {}
      await setStoredOverridesInIDB(memoryOverrides);
    }
    notifyListeners();

    // Push each item to Firestore
    Object.entries(batch).forEach(([orig, item]) => {
      if (orig && item) {
        saveWordOverrideToFirestore(orig, item).catch(() => {});
      }
    });

    const res = await fetch('/api/word-overrides', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batch }),
    });
    return res.ok;
  } catch (e) {
    console.error('Failed to import overrides batch:', e);
    return false;
  }
}

/**
 * Reset/Delete a word override from server, IndexedDB, and local cache
 */
export async function resetWordOverride(
  originalKab: string
): Promise<{ success: boolean; overrides: WordOverridesMap }> {
  const normalizedOriginal = originalKab.trim();
  const lowerOriginal = normalizedOriginal.toLowerCase();

  delete memoryOverrides[normalizedOriginal];
  delete memoryOverrides[lowerOriginal];

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
    } catch {}
    await setStoredOverridesInIDB(memoryOverrides);
  }
  notifyListeners();

  try {
    const res = await fetch(`/api/word-overrides/${encodeURIComponent(normalizedOriginal)}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      const data = await res.json();
      if (data && data.overrides) {
        memoryOverrides = data.overrides;
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
          } catch {}
          await setStoredOverridesInIDB(memoryOverrides);
        }
        notifyListeners();
      }
    }
  } catch (err) {
    console.error('Failed to delete word override from server:', err);
  }

  deleteWordOverrideFromFirestore(normalizedOriginal).catch(() => {});
  return { success: true, overrides: memoryOverrides };
}

/**
 * Completely delete a word from the dictionary (built-in or custom).
 * Marks the word as permanently deleted across local memory, localStorage, IndexedDB,
 * server API, and Firebase Firestore.
 */
export async function deleteWordFromDictionary(
  target: string | { kab: string; originalKab?: string }
): Promise<{ success: boolean; overrides: WordOverridesMap }> {
  let primaryKey = '';
  let altKey = '';

  if (typeof target === 'string') {
    primaryKey = target.trim();
  } else if (target) {
    primaryKey = (target.originalKab || target.kab || '').trim();
    altKey = (target.kab || '').trim();
  }

  if (!primaryKey && !altKey) {
    return { success: false, overrides: memoryOverrides };
  }

  const deletedOverride: WordOverride = {
    kab: altKey || primaryKey,
    fr: '',
    isDeleted: true,
  };

  const keysToMark = new Set<string>();
  if (primaryKey) {
    keysToMark.add(primaryKey);
    keysToMark.add(primaryKey.toLowerCase());
  }
  if (altKey) {
    keysToMark.add(altKey);
    keysToMark.add(altKey.toLowerCase());
  }

  // 1. Immediate in-memory update
  keysToMark.forEach((k) => {
    memoryOverrides[k] = { ...deletedOverride, kab: k };
  });

  // 2. Persist to localStorage and IndexedDB
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
    } catch {}
    await setStoredOverridesInIDB(memoryOverrides);
  }

  // 3. Notify all listeners (including gameData and dictionary view)
  notifyListeners();

  // 4. Remove recorded audio if any
  try {
    if (primaryKey) deleteRecordedAudio(primaryKey).catch(() => {});
    if (altKey && altKey !== primaryKey) deleteRecordedAudio(altKey).catch(() => {});
  } catch {}

  // 5. Persist deletion to server backend
  try {
    for (const key of [primaryKey, altKey].filter(Boolean)) {
      await fetch('/api/word-overrides', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalKab: key,
          kab: key,
          fr: '',
          isDeleted: true,
        }),
      });
    }
  } catch (err) {
    console.error('Failed to notify server of word deletion:', err);
  }

  // 6. Persist deletion to Firebase Firestore
  try {
    for (const k of keysToMark) {
      await saveWordOverrideToFirestore(k, {
        kab: k,
        fr: '',
        isDeleted: true,
      });
    }
  } catch (err) {
    console.error('Failed to sync deleted word to Firestore:', err);
  }

  return { success: true, overrides: memoryOverrides };
}

/**
 * Explicitly synchronize word overrides bidirectionally with Firebase Firestore
 */
export async function syncWordOverridesWithFirestore(): Promise<{ pushed: number; pulled: number }> {
  let pushed = 0;
  let pulled = 0;

  try {
    const cloudOverrides = await fetchAllWordOverridesFromFirestore();
    if (cloudOverrides && Object.keys(cloudOverrides).length > 0) {
      pulled = Object.keys(cloudOverrides).length;
      memoryOverrides = { ...memoryOverrides, ...cloudOverrides };
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryOverrides));
        } catch {}
        await setStoredOverridesInIDB(memoryOverrides);
      }
      notifyListeners();
    }

    // Auto-heal / push any local overrides that aren't yet in Firestore
    for (const [key, val] of Object.entries(memoryOverrides)) {
      if (!cloudOverrides || !cloudOverrides[key.toLowerCase().trim()]) {
        try {
          await saveWordOverrideToFirestore(key, val);
          pushed++;
        } catch (e) {
          console.warn('Could not push word override to Firestore:', key, e);
        }
      }
    }
  } catch (err) {
    console.warn('syncWordOverridesWithFirestore error:', err);
  }

  return { pushed, pulled };
}

/**
 * Get current word overrides map synchronously
 */
export function getWordOverridesMap(): WordOverridesMap {
  return memoryOverrides;
}

/**
 * Subscribe to real-time word override changes
 */
export function subscribeWordOverrides(
  callback: (overrides: WordOverridesMap) => void
): () => void {
  listeners.add(callback);
  callback(memoryOverrides);
  return () => {
    listeners.delete(callback);
  };
}

// Auto-sync on startup in browser
if (typeof window !== 'undefined') {
  syncWordOverridesFromServer();
}
